import { Box, HiddenFromView } from '@stitch-fix/mode-react';
import type { FieldErrors, FieldValues } from 'react-hook-form';

/**
 * Builds the text announced by {@link FormErrorAnnouncer} from react-hook-form's
 * `errors`.
 *
 * Each message is prefixed with its field label so the announcement identifies
 * which field is in error — the messages themselves ("Please enter a password.")
 * are ambiguous when more than one field fails. `labels` is keyed by field name
 * and its key order determines the order fields are announced in, so list them
 * in the order they appear in the form.
 */
export const getFormErrorAnnouncement = <TFieldValues extends FieldValues,>(errors: FieldErrors<TFieldValues>, labels: Record<string, string>): string => Object.entries(labels).map(([name, label]) => {
  const message = errors[name]?.message;
  return typeof message === 'string' && message ? `${label}: ${message}` : '';
}).filter(Boolean).join(' ');
export type FormErrorAnnouncerProps = {
  /**
   * The error text to announce. When empty the live region stays in the DOM but
   * has nothing to announce.
   */
  message?: string;
  /**
   * The number of submission attempts so far — pass react-hook-form's
   * `formState.submitCount`.
   *
   * Used as the `key` of the message element so that each attempt replaces it
   * rather than leaving it untouched. Resubmitting a form without fixing
   * anything produces the same `message`, which React renders as no DOM change
   * at all, and a live region with unchanged contents announces nothing — so
   * without this the second and later failures are silent. That case matters
   * most when the user submits with Enter from the first invalid field, where
   * focus does not move and the announcement is the only feedback they get.
   *
   * Optional so the live region can be rendered on its own, but a form that
   * omits it will not re-announce repeated identical failures.
   */
  attempt?: number;
};

/**
 * A visually hidden `role="alert"` live region used to announce form validation
 * errors to screen reader users.
 *
 * The container is rendered unconditionally so that it is already present in the
 * DOM before any error occurs. Screen readers only reliably announce a
 * `role="alert"` region when the element already exists and its *contents*
 * change — mounting the role and the message at the same moment (which is what
 * conditionally rendered error markup does) is frequently missed, notably by
 * VoiceOver.
 *
 * Needed because mode-react's `TextField` only renders its help text element
 * when there is help text to show, so its `role="alert"` enters the DOM
 * together with the message.
 *
 * The message is keyed by `attempt` so that repeated failures with identical
 * text still change the region's contents and get announced each time.
 *
 * See WCAG 2.1 SC 4.1.3 Status Messages.
 */
export const FormErrorAnnouncer = ({
  message,
  attempt
}: FormErrorAnnouncerProps) => <Box role="alert" aria-atomic="true" data-testid="form-error-announcer">
    {message ? <HiddenFromView key={attempt}>{message}</HiddenFromView> : null}
  </Box>;